import { cn } from "@/lib/utils/cn";
import React from "react";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = [
  ...Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0")),
  "59",
];

type TimePickerProps = {
  readonly value: string; // "HH:MM"
  readonly onChange: (value: string) => void;
};

export const TimePicker: React.FC<TimePickerProps> = ({ value, onChange }) => {
  const parts = value.split(":");
  const h = parts[0] ?? "00";
  const m = parts[1] ?? "00";

  return (
    <div className="flex items-center gap-1">
      <ScrollColumn
        items={HOURS}
        selected={h}
        onSelect={(v) => onChange(`${v}:${m}`)}
      />
      <span className="text-[10px] text-muted-foreground font-medium shrink-0">:</span>
      <ScrollColumn
        items={MINUTES}
        selected={m}
        onSelect={(v) => onChange(`${h}:${v}`)}
      />
    </div>
  );
};

const ScrollColumn: React.FC<{
  items: string[];
  selected: string;
  onSelect: (value: string) => void;
}> = ({ items, selected, onSelect }) => {
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    // Small delay to ensure the element is rendered before scrolling
    requestAnimationFrame(() => {
      const el = ref.current?.querySelector("[data-selected]");
      el?.scrollIntoView({ block: "nearest" });
    });
  }, [selected]);

  return (
    <div
      ref={ref}
      className="flex-1 min-w-0 h-32 overflow-y-auto overscroll-contain rounded-md border bg-muted/20"
      style={{ scrollbarWidth: "thin" }}
    >
      {items.map((item) => (
        <button
          key={item}
          type="button"
          data-selected={item === selected || undefined}
          onClick={() => onSelect(item)}
          className={cn(
            "w-full h-6 text-[11px] tabular-nums rounded cursor-pointer transition-colors",
            item === selected
              ? "bg-primary text-primary-foreground font-medium"
              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
          )}
        >
          {item}
        </button>
      ))}
    </div>
  );
};
